import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MilestonesGridData, MomentData } from '../types';
import { selectSortSelections } from '../slice/selectors';

/**
 * Hook to sort milestones by NAME column
 * Supports:
 * - Alphabetical sorting (A to Z, Z to A)
 * - Risk-based sorting (Low to High Risk, High to Low Risk)
 */
export const useSortByName = (
  data: MilestonesGridData | null
): MilestonesGridData | null => {
  const sortSelections = useSelector(selectSortSelections);
  const nameSelections = sortSelections.name || [];

  return useMemo(() => {
    if (!data || !data.data || data.data.length === 0) {
      return data;
    }

    // Find active sort option (Name column has no pins)
    const sortOption = nameSelections[0]; // Name column only has one selection at a time

    // If no sort option, return data as-is
    if (!sortOption) {
      return data;
    }

    // Clone data to avoid mutation
    const sortedData = { ...data, data: [...data.data] };

    // Define risk level priority (Low to High: In Planning < Green < Yellow < Red)
    const riskPriority: Record<string, number> = {
      'In Planning': 1,
      Green: 2,
      Yellow: 3,
      Red: 4,
      NA: 5
    };

    sortedData.data.sort((a: MomentData, b: MomentData) => {
      // Alphabetical sorting
      if (sortOption === 'aToZ' || sortOption === 'zToA') {
        const nameA = a.metaDetails?.name?.toLowerCase() || '';
        const nameB = b.metaDetails?.name?.toLowerCase() || '';

        if (sortOption === 'aToZ') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      }

      // Risk-based sorting
      if (sortOption === 'lowToHigh' || sortOption === 'highToLow') {
        const riskA = a.metaDetails?.riskLevel || '';
        const riskB = b.metaDetails?.riskLevel || '';

        const priorityA = riskPriority[riskA] || 999; // Unknown risks go last
        const priorityB = riskPriority[riskB] || 999;

        if (sortOption === 'lowToHigh') {
          return priorityA - priorityB;
        } else {
          return priorityB - priorityA;
        }
      }

      return 0;
    });

    return sortedData;
  }, [data, sortSelections.name]);
};
